import { apiClient } from '@/shared/services/client'
import { SITE_URL } from '@/shared/lib/constants'
import type {
  URLAcortada,
  URLAcortadaDetalle,
  URLAcortadaCreateData,
  URLAcortadaUpdateData,
  URLsPaginadas,
  URLCreadaResponse,
  ToggleStatusResponse,
  SuccessResponse,
  AnalyticsURL,
  DashboardStats,
  FiltrosURL,
} from '../types'

export const linkTrackerApi = {
  // =========================================================================
  // Dashboard
  // =========================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get('/link-tracker/analytics/dashboard/')
    return response.data
  },

  // =========================================================================
  // Stats individuales para cards
  // =========================================================================

  async getTotalURLs(): Promise<{ total_urls: number }> {
    const response = await apiClient.get('/link-tracker/analytics/stats/total-urls/')
    return response.data
  },

  async getURLsActivas(): Promise<{ urls_activas: number }> {
    const response = await apiClient.get('/link-tracker/analytics/stats/urls-activas/')
    return response.data
  },

  async getTotalClics(): Promise<{ total_clics: number }> {
    const response = await apiClient.get('/link-tracker/analytics/stats/total-clics/')
    return response.data
  },

  async getClicsHoy(): Promise<{ clics_hoy: number; fecha: string }> {
    const response = await apiClient.get('/link-tracker/analytics/stats/clics-hoy/')
    return response.data
  },

  // =========================================================================
  // URLs CRUD
  // =========================================================================

  async getURLs(params?: Partial<FiltrosURL>): Promise<URLsPaginadas> {
    const response = await apiClient.get('/link-tracker/urls/', { params })
    return response.data
  },

  async getURL(codigo: string): Promise<URLAcortadaDetalle> {
    const response = await apiClient.get(`/link-tracker/urls/${codigo}/`)
    return response.data
  },

  async crearURL(data: URLAcortadaCreateData): Promise<URLCreadaResponse> {
    const response = await apiClient.post('/link-tracker/urls/', data)
    return response.data
  },

  async actualizarURL(codigo: string, data: URLAcortadaUpdateData): Promise<URLAcortada> {
    const response = await apiClient.put(`/link-tracker/urls/${codigo}/`, data)
    return response.data
  },

  async eliminarURL(codigo: string): Promise<SuccessResponse> {
    const response = await apiClient.delete(`/link-tracker/urls/${codigo}/`)
    return response.data
  },

  async toggleURLStatus(codigo: string): Promise<ToggleStatusResponse> {
    const response = await apiClient.patch(`/link-tracker/urls/${codigo}/toggle/`)
    return response.data
  },

  // =========================================================================
  // Analytics
  // =========================================================================

  async getAnalytics(codigo: string, pais?: string): Promise<AnalyticsURL> {
    const response = await apiClient.get(`/link-tracker/analytics/${codigo}/`, {
      params: pais ? { pais } : undefined,
    })
    return response.data
  },

  async exportarExcel(codigo: string): Promise<Blob> {
    const response = await apiClient.get(`/link-tracker/analytics/${codigo}/excel/`, {
      responseType: 'blob'
    })
    return response.data
  },

  // =========================================================================
  // Helpers
  // =========================================================================

  /**
   * Descarga el archivo Excel de analytics
   */
  async descargarExcel(codigo: string): Promise<void> {
    const blob = await this.exportarExcel(codigo)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `analytics_${codigo}_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /**
   * Copia texto al portapapeles
   */
  async copiarAlPortapapeles(texto: string): Promise<boolean> {
    // Fallback que funciona en más contextos
    const textarea = document.createElement('textarea')
    textarea.value = texto
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.top = '0'
    textarea.setAttribute('readonly', '')
    document.body.appendChild(textarea)
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)

    let success = false
    try {
      success = document.execCommand('copy')
    } catch {
      success = false
    }

    document.body.removeChild(textarea)

    // Si el fallback falla, intentar con la API moderna
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(texto)
        success = true
      } catch {
        success = false
      }
    }

    return success
  },

  /**
   * Obtiene el QR como Blob
   */
  async obtenerQRBlob(codigo: string): Promise<Blob> {
    const response = await apiClient.get(`/link-tracker/urls/${codigo}/qr/`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Descarga el QR como archivo PNG
   */
  async descargarQR(codigo: string): Promise<void> {
    const blob = await this.obtenerQRBlob(codigo)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `qr_${codigo}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /**
   * Genera la URL corta completa
   */
  getURLCorta(codigo: string): string {
    return `${SITE_URL}/c/${codigo}`
  },

  /**
   * Genera la URL con tracking para Twilio
   */
  getURLConTracking(codigo: string, campana?: string): string {
    let url = `${SITE_URL}/c/${codigo}?tel={{1}}`
    if (campana) {
      url += `&campana=${encodeURIComponent(campana)}`
    }
    return url
  }
}
